import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Quote, Facebook, Twitter, Linkedin, Mail, Link2, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import GuidedAudioPlayer from "../components/GuidedAudioPlayer";
import MeditationTimer from "../components/MeditationTimer";
import { getPostBySlug, CATEGORY_META, BLOG_POSTS, AUTHOR, getSeriesNav } from "../blog";

function Block({ block }) {
  if (block.type === "h") {
    return <h2 className="font-serif-brand text-2xl md:text-3xl text-purple mt-10 mb-3">{block.text}</h2>;
  }
  if (block.type === "q") {
    return (
      <blockquote className="my-8 pl-6 border-l-4 border-[var(--teal)] italic text-neutral-700 text-lg font-serif-brand">
        {block.text}
      </blockquote>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="my-5 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-neutral-700 leading-relaxed">
            <span className="mt-2 w-2 h-2 rounded-full bg-[var(--teal)] flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-neutral-600 leading-[1.85] my-4 text-[1.02rem]">{block.text}</p>;
}

function ShareBar({ title }) {
  const [copied, setCopied] = React.useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const enc = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title);

  const links = [
    { icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc}` },
    { icon: Twitter, label: "X", href: `https://twitter.com/intent/tweet?url=${enc}&text=${encTitle}` },
    { icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}` },
    { icon: Mail, label: "Email", href: `mailto:?subject=${encTitle}&body=${enc}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mt-10 pt-8 border-t border-neutral-100">
      <span className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 mr-1">
        Share this article
      </span>
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${l.label}`}
          title={`Share on ${l.label}`}
          className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-[var(--teal)] hover:text-white hover:border-[var(--teal)] hover:-translate-y-0.5 transition-all"
        >
          <l.icon size={17} />
        </a>
      ))}
      <button
        onClick={copy}
        aria-label="Copy link"
        title="Copy link"
        className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-[var(--purple)] hover:text-white hover:border-[var(--purple)] hover:-translate-y-0.5 transition-all"
      >
        {copied ? <Check size={17} /> : <Link2 size={17} />}
      </button>
      {copied && <span className="text-xs text-[var(--teal-dark)] font-semibold">Link copied!</span>}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const meta = CATEGORY_META[post.category];
  const related = BLOG_POSTS.filter(
    (p) => p.category === post.category && p.slug !== post.slug
  ).slice(0, 3);
  const series = getSeriesNav(post.slug);

  return (
    <article>
      {/* Hero image */}
      <section className="relative h-[42vh] md:h-[52vh] min-h-[320px] overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto w-full px-5 pb-8 md:pb-12">
            <h1 className="font-serif-brand text-3xl md:text-5xl text-white leading-tight drop-shadow">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-white/85 text-xs uppercase tracking-wide-sm mt-4">
              <span>{post.date}</span>
              <span className="flex items-center gap-1">
                <Clock size={13} /> {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[var(--teal-dark)] font-semibold tracking-wide-sm uppercase text-xs mb-8 hover:gap-3 transition-all"
        >
          <ArrowLeft size={14} /> Back to Wellness Insights
        </Link>

        <p className="text-lg text-neutral-700 font-serif-brand italic leading-relaxed mb-6">
          {post.excerpt}
        </p>
        <div className="brand-rule mb-2" />

        {/* Series indicator (meditation path) */}
        {series && (
          <div className="mt-6 mb-2 flex items-center gap-3">
            <span className="px-3 py-1 rounded-full bg-[#eef1fb] text-[#5a67a8] text-[11px] font-semibold tracking-wide-sm uppercase">
              Meditation Path · Part {series.current} of {series.total}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
              <div
                className="h-full bg-[#5a67a8] rounded-full transition-all"
                style={{ width: `${(series.current / series.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Guided audio player */}
        {post.audio && (
          <div className="my-8">
            <GuidedAudioPlayer label={post.audio.label} lines={post.audio.lines} />
          </div>
        )}

        {/* Practice timer (meditation articles) */}
        {post.category === "meditation" && (
          <div className="my-8">
            <MeditationTimer />
          </div>
        )}

        {post.content.map((block, i) => (
          <Block key={i} block={block} />
        ))}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-neutral-100">
            {post.tags.map((t) => (
              <span
                key={t}
                className="px-3 py-1.5 rounded-full bg-[#f3edf5] text-[var(--purple)] text-[11px] tracking-wide-sm"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Social share */}
        <ShareBar title={post.title} />

        {/* Author byline */}
        <div className="mt-10 flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white rounded-xl border border-neutral-100 shadow-sm p-6 md:p-8">
          <img
            src={AUTHOR.photo}
            alt={AUTHOR.name}
            className="w-24 h-24 rounded-full object-cover ring-2 ring-[var(--teal)] flex-shrink-0"
          />
          <div className="text-center sm:text-left">
            <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--teal-dark)]">
              Written by
            </p>
            <h3 className="font-serif-brand text-2xl text-purple mt-1">{AUTHOR.name}</h3>
            <p className="text-[13px] text-neutral-500 italic mt-0.5">{AUTHOR.role}</p>
            <p className="text-neutral-600 leading-relaxed mt-3 text-[0.95rem]">{AUTHOR.bio}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-xl bg-[#f7fbfa] border border-neutral-100 p-8 text-center">
          <Quote className="mx-auto text-[var(--teal)] mb-3" size={28} />
          <h3 className="font-serif-brand text-2xl text-purple">Ready to begin your journey?</h3>
          <p className="text-neutral-600 mt-3 max-w-lg mx-auto">
            Book a complimentary call with Coach Kam to explore a personalized path to your
            healthiest, most balanced self.
          </p>
          <Link
            to="/book"
            className="btn-brand btn-teal mt-6"
          >
            Book a Call <ArrowRight size={16} />
          </Link>
        </div>

        {/* Series navigation (meditation path Part 1 -> 4) */}
        {series && (series.prev || series.next) && (
          <div className="mt-10 pt-8 border-t border-neutral-100">
            <p className="text-center text-[11px] tracking-[0.22em] uppercase text-[#5a67a8] font-semibold mb-4">
              Continue the Meditation Path
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {series.prev ? (
                <Link
                  to={`/blog/${series.prev.slug}`}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#5a67a8] hover:-translate-y-0.5 transition-all"
                >
                  <ArrowLeft size={18} className="text-[#5a67a8] flex-shrink-0" />
                  <span className="text-left">
                    <span className="block text-[10px] tracking-wide-sm uppercase text-neutral-400">
                      Part {series.prev.part}
                    </span>
                    <span className="block text-sm font-semibold text-purple leading-snug">
                      {series.prev.title}
                    </span>
                  </span>
                </Link>
              ) : (
                <span className="hidden sm:block" />
              )}
              {series.next && (
                <Link
                  to={`/blog/${series.next.slug}`}
                  className="group flex items-center justify-end gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#5a67a8] hover:-translate-y-0.5 transition-all text-right"
                >
                  <span>
                    <span className="block text-[10px] tracking-wide-sm uppercase text-neutral-400">
                      Part {series.next.part}
                    </span>
                    <span className="block text-sm font-semibold text-purple leading-snug">
                      {series.next.title}
                    </span>
                  </span>
                  <ArrowRight size={18} className="text-[#5a67a8] flex-shrink-0" />
                </Link>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#f7fbfa] py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-5">
            <h2 className="font-serif-brand text-3xl md:text-4xl text-purple text-center mb-3">
              More in {meta.label}
            </h2>
            <div className="brand-rule mx-auto mb-12" />
            <div className="grid gap-8 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group block bg-white rounded-xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-brand text-xl text-purple leading-snug">{r.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-2 text-[var(--teal-dark)] font-semibold tracking-wide-sm uppercase text-[11px]">
                      Read <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
